import { useEmotionWheelStore } from '../store/useEmotionWheelStore';
import { motion, AnimatePresence } from 'framer-motion';
import { ResetWheelButton } from '../reset-wheel-button';
import StatsAndStreak, { StatsAndStreakContainer } from '@/components/StatsAndStreak';
import { useQuery } from '@apollo/client';
import {
  ComponentEmotionWheelFinalState,
  FetchUserStreakQuery,
  FetchUserStreakQueryVariables,
} from '@/graphql/generated/graphql';
import { QUERY_FETCH_USER_STREAK } from '@/graphql/queries/daily/completed-tasks';
import { useHomeStore } from '@/pages/home/useHome';
import { useEffect } from 'react';
import { parseAdviceWithLinks } from '@/utils/navigation';

export const MessageContainer = () => {
  const { emotionWheelpage, L1, L2 } = useEmotionWheelStore();
  const { page: homePage, fetchHomePage, dailyEmotionWheelCompleted, handleEmotionWheel } = useHomeStore();

  const { data: emotionWheelData, refetch } = useQuery<FetchUserStreakQuery, FetchUserStreakQueryVariables>(
    QUERY_FETCH_USER_STREAK,
    {
      variables: {
        type: 'EMOTIONAL_WHEEL',
      },
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    fetchHomePage();
    handleEmotionWheel();
  }, [fetchHomePage, handleEmotionWheel]);

  useEffect(() => {
    if (L2) {
      refetch();
    }
  }, [L2, refetch]);

  const message = !L1 ? 'initial' : L1 && !L2 ? 'L1' : 'L2';
  const title = `${message}Title` as 'initialTitle' | 'L1Title' | 'L2Title';

  // Animation variants for messages
  const variants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  return (
    <div className="mx-auto grid h-full items-start  justify-center md:mt-10 lg:mt-28 ">
      <AnimatePresence mode="wait">
        <motion.div
          key={message}
          variants={variants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          transition={{ ease: 'easeOut', duration: 0.5 }}>
          <p key={title} className=" text-center font-larken text-sh-md">
            {emotionWheelpage?.messages?.[title]?.replace('${emotion}', L2?.emotion || '') || ''}
          </p>
          <div
            key={message}
            className="mt-sh-15 flex text-center [&>p]:text-sh-xxs"
            dangerouslySetInnerHTML={{
              __html: L2
                ? parseAdviceWithLinks(L2?.advice || '', L2?.finalState as ComponentEmotionWheelFinalState)
                : emotionWheelpage?.messages?.[message] || '',
            }}
          />
          {dailyEmotionWheelCompleted || L2 ? (
            <StatsAndStreakContainer className="mt-sh-30">
              <StatsAndStreak
                type="EMOTION_WHEEL"
                value={emotionWheelData?.fetchUserStreak?.total || 0}
                unit={homePage?.finishingCard?.finishingCardEmotionWheel?.emotions || 'Emotions'}
                title={homePage?.finishingCard?.finishingCardEmotionWheel?.youHaveTracked || "You've tracked"}
                color="EMOTION_WHEEL"
              />
              <StatsAndStreak
                type="STREAK"
                value={emotionWheelData?.fetchUserStreak?.streak || 0}
                unit={homePage?.finishingCard?.finishingCardEmotionWheel?.days || 'Days'}
                title={homePage?.finishingCard?.finishingCardEmotionWheel?.yourStreak || 'Your streak'}
                color="EMOTION_WHEEL"
              />
            </StatsAndStreakContainer>
          ) : null}
          <div className="flex items-start self-start">
            <ResetWheelButton show={!!L1} />
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
};
